/**
 * ASCII JavaScript identifier pattern used for object keys and member access.
 */
const VALID_IDENTIFIER_RE = /^[a-z_$][\w$]*$/i;

/**
 * String escape replacements used by single-quoted JavaScript snippets.
 */
const JS_STRING_ESCAPES: [searchValue: string, replaceValue: string][] = [
	['\\', String.raw`\\`],
	['\'', String.raw`\'`],
	['\r', String.raw`\r`],
	['\n', String.raw`\n`],
	['\t', String.raw`\t`],
	['\u2028', String.raw`\u2028`],
	['\u2029', String.raw`\u2029`],
];

/**
 * Checks whether a value can be emitted as a JavaScript identifier.
 */
export function isValidJavascriptIdentifier(value: string): boolean {
	return VALID_IDENTIFIER_RE.test(value);
}

/**
 * Formats property access using dot notation when possible and bracket notation otherwise.
 *
 * Bracket notation is used for generated collection accessors that are not valid
 * JavaScript identifiers, such as names with spaces or hyphens.
 */
export function formatJavascriptMemberExpression(target: string, property: string): string {
	return isValidJavascriptIdentifier(property)
		? `${target}.${property}`
		: `${target}[${quoteJavascriptString(property)}]`;
}

/**
 * Formats an object key using identifier syntax when it is safe.
 */
export function formatJavascriptObjectKey(key: string): string {
	return isValidJavascriptIdentifier(key) ? key : quoteJavascriptString(key);
}

/**
 * Quotes and escapes a string for single-quoted JavaScript snippets.
 */
export function quoteJavascriptString(value: string): string {
	let result = value;

	// Escape backslashes first so existing escape sequences are preserved literally.
	for (const [searchValue, replaceValue] of JS_STRING_ESCAPES) {
		result = result.replaceAll(searchValue, replaceValue);
	}

	return `'${result}'`;
}
